<h2 lvrPadding [padHorizontal]="32" [padVertical]="0">{{ 'assignment.create_fds.title' | transloco }}</h2>

@if (client(); as client) {
	<p class="client-name">
		{{ client.firstName }} {{ client.name }}
		{{ validDateOfBirth() ? '| ' + (client.dateOfBirth | date: dateDisplayFormat) : '' }}
	</p>
}

<form class="form-container" [formGroup]="form" lvrAutoSave="fds" [autoLoad]="false" [entityId]="draftId()">
	<section class="form-grid">
		<!-- Datum der Beauftragung -->
		@let dateCtrl = form.get('dateOfCommission');
		<lvr-field [label]="'assignment.create_fds.dateOfCommission' | transloco | appendAsterisk">
			<mat-form-field lvrInput [lvrError]="hasError(dateCtrl, 'minDateExceeded', 'maxDateExceeded')">
				<input
					matInput
					[matDatepicker]="picker"
					[min]="minDateOfCommission()"
					[max]="maxDateOfCommission()"
					formControlName="dateOfCommission"
					placeholder="01.07.2025"
					[attr.aria-label]="'assignment.create_fds.dateOfCommission' | transloco"
					[errorStateMatcher]="errorStateMatcher"
				/>
				@if (hasError(dateCtrl, 'minDateExceeded')) {
					<mat-error>{{
						'assignment.create_fds.errors.min_date'
							| transloco: { min: (minDateOfCommission() | date: dateDisplayFormat) }
					}}</mat-error>
				} @else if (hasError(dateCtrl, 'maxDateExceeded')) {
					<mat-error>{{ 'assignment.create_fds.errors.max_date' | transloco }}</mat-error>
				} @else {
					<mat-hint>{{ dateFormatHint }}</mat-hint>
				}
				<mat-datepicker-toggle
					matIconSuffix
					[for]="picker"
					[attr.aria-label]="'assignment.create_fds.dateOfCommission' | transloco"
				></mat-datepicker-toggle>
				<mat-datepicker #picker></mat-datepicker>
			</mat-form-field>
		</lvr-field>

		<!-- Auftraggeber -->
		@let sponsorCtrl = form.get('sponsorId');
		<lvr-field [label]="'base.field.sponsor' | transloco | appendAsterisk">
			<lvr-searchable-select
				formControlName="sponsorId"
				[options]="sponsors()"
				[optionLabel]="sponsorLabel"
				[placeholder]="'base.field.select_please' | transloco"
				[searchPlaceholder]="'base.field.search_placeholder' | transloco"
				[noResultsText]="'base.field.no_results' | transloco"
				[ariaLabel]="'base.field.sponsor' | transloco"
				[required]="true"
				[hasError]="sponsors().length > 0 && hasError(sponsorCtrl, 'required')"
				[errorMessage]="'base.form.error.sponsor_required' | transloco"
				[hintText]="sponsors().length === 0 ? ('base.form.hint.no_initiators' | transloco) : ''"
			></lvr-searchable-select>
		</lvr-field>

		<!-- Grundlage -->
		@let legalFoundationCtrl = form.get('legalFoundationId');
		<lvr-field [label]="'base.field.legal_foundation' | transloco | appendAsterisk">
			<lvr-searchable-select
				formControlName="legalFoundationId"
				[options]="availableLegalFoundation()"
				[optionLabel]="legalFoundationLabel"
				[placeholder]="'base.field.select_please' | transloco"
				[searchPlaceholder]="'base.field.search_placeholder' | transloco"
				[noResultsText]="'base.field.no_results' | transloco"
				[ariaLabel]="'base.field.legal_foundation' | transloco"
				[required]="true"
				[hasError]="
					!!sponsorCtrl!.value && availableLegalFoundation().length > 0 && hasError(legalFoundationCtrl, 'required')
				"
				[errorMessage]="'base.form.error.legal_foundation_required' | transloco"
				[hintText]="
					!sponsorCtrl!.value
						? ('base.form.hint.no_sponsor_selected' | transloco)
						: availableLegalFoundation().length === 0
							? ('base.form.hint.no_legal_foundation' | transloco)
							: ('base.field.legal_foundation_hint' | transloco)
				"
			></lvr-searchable-select>
		</lvr-field>

		<!-- Relevante Behinderungsart -->
		<lvr-field [label]="'base.field.type_of_disability_relevant_to_care' | transloco">
			<mat-form-field lvrInput [attr.aria-label]="'base.field.select_please' | transloco">
				<mat-select
					#sel="matSelect"
					formControlName="careRelatedHandicapId"
					[placeholder]="'base.field.type_of_disability_relevant_to_care' | transloco"
				>
					<mat-select-trigger>
						{{ getDisabilityKindName(sel.value) }}
						<span class="text-sm differentiation">{{ getDisabilityKindDifferentiation(sel.value) }}</span>
					</mat-select-trigger>

					@for (option of disabilityKinds(); track option.id) {
						<mat-option class="option" [value]="option.id">
							<span class="remark">{{ option.disabilityType?.remark ?? '' }}</span>
							<span class="text-sm">{{ option.categoryDiagnosis?.description ?? '-' }}</span>
						</mat-option>
					}
				</mat-select>
				@if (disabilityKinds().length === 0) {
					<mat-hint>
						{{ 'assignment.edit.no_disability_kinds_prefix' | transloco }}
						<a
							[routerLink]="['/desk/client', client().id, 'disabilities', 'type', 'add']"
							[queryParams]="queryParams"
							(click)="saveDraftBeforeNavigation()"
						>
							{{ 'assignment.edit.no_disability_kinds_link' | transloco }}
						</a>
						{{ 'assignment.edit.no_disability_kinds_suffix' | transloco }}
					</mat-hint>
				}
			</mat-form-field>
		</lvr-field>

		<!-- Stellungnahme -->
		@if (showInitialStatementSelect()) {
			@let initialStatementCtrl = form.get('initialStatement');
			<lvr-field [label]="'assignment.create_fds.statement_type' | transloco | appendAsterisk">
				<mat-form-field
					lvrInput
					[lvrError]="hasError(initialStatementCtrl, 'required')"
					[attr.aria-label]="'assignment.create_fds.statement_type' | transloco"
				>
					<mat-select
						formControlName="initialStatement"
						[placeholder]="'base.field.select_please' | transloco"
						aria-required="true"
						[errorStateMatcher]="matcher"
					>
						<mat-option [value]="true">{{ 'assignment.create_fds.statement_type_initial' | transloco }}</mat-option>
						<mat-option [value]="false">{{ 'assignment.create_fds.statement_type_follow_up' | transloco }}</mat-option>
					</mat-select>
					@if (hasError(initialStatementCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			<lvr-field></lvr-field>
		}

		<!-- Merkblatt Sozialdatenschutz -->
		<lvr-data-protection
			formControlName="clarification"
			checkboxLabel="base.field.social_data_protection_info_hint"
			label="base.field.social_data_protection_info"
			[showAsterisk]="false"
			[minDateInput]="minDateOfCommission()"
			[maxDateInput]="maxDateOfCommission()"
		></lvr-data-protection>

		<!-- Dokumente -->
		@let documentsCtrl = form.get('documents');
		<lvr-field
			[label]="
				documentsRequired()
					? ('base.field.documents' | transloco | appendAsterisk)
					: ('base.field.documents' | transloco)
			"
		>
			<lvr-document-upload
				[maxFileCount]="maxFileCount"
				[hasError]="hasFileError(documentsCtrl, 'requiredFile', 'maxFiles', 'maxFileSize', 'allowedFileTypes')"
				[multiple]="true"
				[disabled]="!canManageAssignmentDocuments()"
				formControlName="documents"
				[showIcon]="true"
				[ownerId]="client().uuid"
				[maxFileSize]="maxFileSize"
				[subjectType]="subjectType"
			>
			</lvr-document-upload>
		</lvr-field>
	</section>

	<p lvrPadding [padHorizontal]="0" [padVertical]="24">{{ 'base.form.required' | transloco }}</p>

	<!-- Actions -->
	<section class="actions">
		<lvr-default-actions
			[disableSave]="form.invalid || !canCreateAssignmentsPermission()"
			[showLoading]="loading()"
			(save)="save()"
			(cancelSave)="cancel()"
		>
			<lvr-restore-actions entityType="fds" [entityId]="draftId()"></lvr-restore-actions>
		</lvr-default-actions>
	</section>
</form>
